import React from 'react';
import { CardCurve } from '../types/card';

interface RetentionCurveChartProps {
  curve: CardCurve;
  desiredRetention?: number;
}

export const RetentionCurveChart: React.FC<RetentionCurveChartProps> = ({
  curve,
  desiredRetention = 0.9,
}) => {
  const points = curve.points || [];

  if (points.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-slate-400">
        暂无足够历史数据计算遗忘曲线
      </div>
    );
  }

  // Dimensions
  const width = 420;
  const height = 180;
  const padding = { top: 20, right: 25, bottom: 30, left: 40 };

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const maxDays = Math.max(...points.map((p) => p.days), 7);
  const minDays = 0;

  // Scales
  const getX = (days: number) =>
    padding.left + ((days - minDays) / (maxDays - minDays || 1)) * innerWidth;
  const getY = (retention: number) =>
    padding.top + (1 - Math.max(0, Math.min(1, retention))) * innerHeight;

  // Generate SVG path for curve
  const pathD = points.reduce((acc, point, idx) => {
    const x = getX(point.days);
    const y = getY(point.retention);
    return idx === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
  }, '');

  // Fill area path
  const areaD = `${pathD} L ${getX(points[points.length - 1].days)} ${padding.top + innerHeight} L ${getX(points[0].days)} ${padding.top + innerHeight} Z`;

  // Priority: backend curve.desired_retention > prop desiredRetention > default 0.9
  const effectiveRetention = curve.desired_retention ?? desiredRetention ?? 0.9;
  const desiredY = getY(effectiveRetention);

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 text-xs">
        <div>
          <span className="font-semibold text-slate-700">FSRS 遗忘曲线预测</span>
          {curve.estimated && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-medium">
              模型估计值
            </span>
          )}
        </div>
        <div className="text-slate-500 font-mono">
          稳定性 S: <span className="font-semibold text-slate-700">{curve.stability ? curve.stability.toFixed(2) : '-'}天</span> | 难度 D: <span className="font-semibold text-slate-700">{curve.difficulty ? curve.difficulty.toFixed(2) : '-'}</span>
        </div>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto overflow-visible">
        {/* Background Grid Lines */}
        {[1.0, 0.9, 0.7, 0.5].map((val) => {
          const y = getY(val);
          return (
            <g key={val}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e2e8f0"
                strokeDasharray="2 2"
              />
              <text
                x={padding.left - 6}
                y={y + 3}
                textAnchor="end"
                className="text-[10px] fill-slate-400 font-mono"
              >
                {Math.round(val * 100)}%
              </text>
            </g>
          );
        })}

        {/* Desired Retention Reference Line */}
        <line
          x1={padding.left}
          y1={desiredY}
          x2={width - padding.right}
          y2={desiredY}
          stroke="#10b981"
          strokeWidth="1.2"
          strokeDasharray="4 3"
        />
        <text
          x={width - padding.right}
          y={desiredY - 4}
          textAnchor="end"
          className="text-[9px] fill-emerald-600 font-medium"
        >
          目标记忆率 ({Math.round(effectiveRetention * 100)}%)
        </text>

        {/* Shaded Area under Curve */}
        <path d={areaD} fill="url(#gradientCurve)" opacity="0.18" />

        <defs>
          <linearGradient id="gradientCurve" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Curve Path */}
        <path
          d={pathD}
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {points.map((pt, i) => (
          <circle
            key={i}
            cx={getX(pt.days)}
            cy={getY(pt.retention)}
            r="3.5"
            className="fill-white stroke-blue-600 stroke-2 hover:r-5 transition-all"
          >
            <title>{`第 ${pt.days.toFixed(1)} 天: 预计记忆率 ${(pt.retention * 100).toFixed(1)}%`}</title>
          </circle>
        ))}

        {/* Bottom X-axis line & labels */}
        <line
          x1={padding.left}
          y1={padding.top + innerHeight}
          x2={width - padding.right}
          y2={padding.top + innerHeight}
          stroke="#cbd5e1"
        />
        <text
          x={padding.left}
          y={height - 8}
          className="text-[10px] fill-slate-400"
        >
          0天
        </text>
        <text
          x={width - padding.right}
          y={height - 8}
          textAnchor="end"
          className="text-[10px] fill-slate-400"
        >
          {Math.round(maxDays)}天
        </text>
      </svg>

      <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
        * 曲线基于真实 FSRS 记忆力衰减公式计算，随间隔天数增加，预计记忆保持率呈负幂律下降。
      </p>
    </div>
  );
};
